import { Box } from '@material-ui/core';
import { Status } from 'copilot-design-system';
import React from 'react';
import { DropEvent } from 'react-dropzone-latest';
import { FiLink as LinkIcon } from 'react-icons/fi';
import { useDispatch } from 'react-redux';

import { FILE_ACCESS_LEVEL, FILE_TYPE } from 'src/constants/fileConsts';
import { FlagsContext } from 'src/context';
import { useProgressUpload } from 'src/hooks/useProgressUpload';
import { FileIconRenderer } from 'src/legacy/components/AgGrid/Renderer/FileIconRenderer';
import { FileDropzone } from 'src/legacy/components/FileDropzone';
import {
  AviIcon,
  CsvIcon,
  DocIcon,
  GenericIcon,
  GenericSetIcon,
  GifIcon,
  IsoIcon,
  JpgIcon,
  MovIcon,
  Mp3Icon,
  Mp4Icon,
  PdfIcon,
  PngIcon,
  SVGIcon,
  TxtIcon,
  XlxIcon,
  ZipIcon,
} from 'src/legacy/components/Icons';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import {
  saveUploadedFiles,
  updateFileDragging,
  updateHoveredRowNode,
} from 'src/store/files/actions';
import { FileCellRendererProps } from 'src/store/files/types';
import { alertSnackbar } from 'src/store/ui/actions';
import { FileUtils } from 'src/utils';
import S3Utils from 'src/utils/S3Utils';

const fileExtensionToIcon: {
  [key: string]: React.ComponentType<{ style: React.CSSProperties }>;
} = {
  avi: AviIcon,
  doc: DocIcon,
  docx: DocIcon,
  generic: GenericIcon,
  genericSet: GenericSetIcon,
  iso: IsoIcon,
  jpg: JpgIcon,
  jpeg: JpgIcon,
  mov: MovIcon,
  mp3: Mp3Icon,
  pdf: PdfIcon,
  png: PngIcon,
  txt: TxtIcon,
  zip: ZipIcon,
  svg: SVGIcon,
  link: LinkIcon,
  mp4: Mp4Icon,
  gif: GifIcon,
  csv: CsvIcon,
  xlx: XlxIcon,
  xlsx: XlxIcon,
  xls: XlxIcon,
};

interface IconWrapperProps {
  extension: string;
  size?: number;
}

export const IconWrapper = (props: IconWrapperProps) => {
  const { extension, size = 21 } = props;
  const Icon = fileExtensionToIcon[extension] || fileExtensionToIcon.generic;
  const iconProps = {
    style: {
      fontSize: size,
      fill: 'none',
    },
  };
  return <Icon {...iconProps} />;
};

export const FileCellRendererV2: React.FC<FileCellRendererProps> = ({
  data,
  value,
  channelId = '',
  selectedFilePath = '',
  isClient,
  fileSettings,
}) => {
  const { EnableFolderLocking } = React.useContext(FlagsContext);
  const isFolderUpdateDisabled =
    isClient && Boolean(fileSettings?.clientFolderUpdateDisabled);
  const dispatch = useDispatch();

  const { setUploadProgress, startUploadFiles, exitUploadFiles } =
    useProgressUpload();

  const { node: rowNode } = data;
  const fileType =
    data.fileKey === '__placeholder__' ? FILE_TYPE.FOLDER : FILE_TYPE.FILE;

  const handleFilesDrop = async (
    acceptedFiles: Array<any>,
    _: any,
    dropEvt: DropEvent,
  ) => {
    if (fileType !== FILE_TYPE.FOLDER) return;
    dispatch(updateFileDragging(false));
    dropEvt.stopPropagation();

    // Check if client is trying to upload into a locked folder
    if (
      EnableFolderLocking &&
      isClient &&
      data.clientPermissions === FILE_ACCESS_LEVEL.READ_ONLY
    ) {
      dispatch(
        alertSnackbar({
          errorMessage: 'File cannot be uploaded, folder is read-only.',
        }),
      );
      return;
    }

    const targetPath = data.path
      ? data.path.join('/')
      : selectedFilePath?.split('/').join('/');
    const filesPaths = await S3Utils.uploadFilesToS3({
      acceptedFiles,
      targetPath,
      startUploadFiles,
      exitUploadFiles,
      setUploadProgress,
      ignoreFolders: isFolderUpdateDisabled,
    });
    console.info('saving filesPaths to channel', filesPaths, channelId);
    if (channelId) {
      dispatch(saveUploadedFiles(filesPaths, channelId));
    }
  };

  const handleFilesDragEnter = () => {
    if (fileType !== FILE_TYPE.FOLDER) return;
    dispatch(updateHoveredRowNode(rowNode));
  };

  const handleFileDragLeave = () => {
    if (fileType !== FILE_TYPE.FOLDER) return;
    dispatch(updateHoveredRowNode(null));
  };

  return (
    <FileDropzone
      noClick
      onDragEnter={handleFilesDragEnter}
      onDragLeave={handleFileDragLeave}
      onDrop={handleFilesDrop}
      disabled={isClient && Boolean(fileSettings?.clientFileAddDisabled)}
    >
      <Box
        ml="25px"
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        zIndex={1}
        height="100%"
      >
        <FileIconRenderer file={data} />

        <BaseTypography
          className="overflow-hidden text-ellipsis"
          style={{ marginLeft: 10 }}
        >
          {value.name}
        </BaseTypography>

        {EnableFolderLocking &&
          fileType === FILE_TYPE.FOLDER &&
          FileUtils.isReadOnly(data) && (
            <Status
              className="ml-3 shrink-0"
              icon="Authentication"
              label="Locked"
              showIcon
              status="info"
            />
          )}
      </Box>
    </FileDropzone>
  );
};

export default FileCellRendererV2;
